import { Alignment, Borders, Cell, FillPattern, Font, Row, Workbook, Worksheet } from 'exceljs';
import { get, set } from 'lodash';
import { saveAs } from 'file-saver';
import { CONTRIBUTION_LEVELS } from '@/constants';
import { sumByField, getCurrentDayString } from '@/helpers';
import { AssessmentPeriodDTO, UserContributionLevelPeriodDTO, UserContributionLevelDTO } from '@/models';
import { fetchPeriodsContributionLevels } from '@/services';
import { STYLES, NA } from './project-exporting-config';

type Style = {
  fill?: FillPattern;
  border?: Partial<Borders>;
  font?: Partial<Font>;
  alignment?: Partial<Alignment>;
};

type callbackFn = (cell: Cell, index: number) => void;
const EMPTY = '';

export default class ExportByDrop {
  private workbook = new Workbook();
  private ws: Worksheet;
  private usersExport: UserContributionLevelDTO[] = [];
  private columnWidths = [8, 30, ...Array(5).fill(20)];
  private periodsContributions: UserContributionLevelPeriodDTO[] = [];

  constructor(users: UserContributionLevelDTO[]) {
    this.usersExport = users;
  }

  async generateXLSXFile(): Promise<void> {
    const { data: periodsContributionsData } = await fetchPeriodsContributionLevels();
    this.periodsContributions = periodsContributionsData;
    this.ws = this.workbook.addWorksheet('Export by Pollens');
    this.addSummaryInformationToWorkSheet();
    this.addEmptyRow();
    this.addEmptyRow();
    this.addEmptyRow();
    this.addInformationToWorksheet();
    this.setColumnWidth();
    this.addBorder();
    try {
      const buf = await this.workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, this.exportFileName);
    } catch (error) {
      console.log(error);
    }
  }

  addRow(data: unknown[], style?: Style, callback?: callbackFn): Row {
    const row = this.ws.addRow(data);
    let maxHeight = 20;
    row.eachCell((cell, index) => {
      if (cell.value !== EMPTY) {
        if (typeof cell.value === 'number') {
          cell.alignment = STYLES.middleRightWrappedAlignment;
        } else {
          if (cell.value) {
            const values = cell.value.toString().split(/[\n\r]+/);

            for (const value of values) {
              let height = value.length;

              if (cell.font && cell.font.bold) {
                height *= 1.08;
              }
              maxHeight = Math.max(maxHeight, height);
            }
          }

          cell.alignment = STYLES.middleLeftWrappedAlignment;
        }
        if (cell.value === NA) {
          cell.font = STYLES.naFont;
          cell.alignment = STYLES.middleRightWrappedAlignment;
        }
        this.addStyleToCell(cell, style);
        if (callback) {
          callback(cell, index);
        }
      }
    });
    row.height = maxHeight;
    return row;
  }

  getLastestAssessmentPeriod(): AssessmentPeriodDTO {
    return this.periodsContributions?.reduce((latest, current) => {
      const latestDate = new Date(latest.assessmentPeriod.endDate);
      const currentDate = new Date(current.assessmentPeriod.endDate);
      const isLatestDateAfterCurrentDate = latestDate > currentDate;
      return isLatestDateAfterCurrentDate ? latest : current;
    }, this.periodsContributions?.[0])?.assessmentPeriod;
  }

  getPreviousDrops(userIds?: number[]): number {
    const latestAssessmentPeriod = this.getLastestAssessmentPeriod();
    if (userIds?.length) {
      return this.periodsContributions
        .filter(pc => userIds.includes(pc.user.id) && pc.assessmentPeriod.id !== latestAssessmentPeriod.id)
        .reduce((total, pc) => total + pc.periodAccumulatedDrops, 0);
    }
    return this.periodsContributions
      .filter(pc => pc.assessmentPeriod.id !== latestAssessmentPeriod.id)
      .reduce((total, pc) => total + pc.periodAccumulatedDrops, 0);
  }

  addSummaryInformationToWorkSheet(): void {
    const header = ['', '', '', 'Redeemable Pollens', 'Previous Pollens', 'Latest Earn', 'Total Pollens'];
    this.addTableHeaderRow(header);
    const totalRedeemableDrops = sumByField(this.usersExport, 'redeemableDrops');
    const totalDrops = sumByField(this.usersExport, 'accumulatedDrops');
    const userIds = this.usersExport.map(user => user.user.id);
    const totalPreviousDrops = this.getPreviousDrops(userIds);
    const totalLatestEarnedDrops = totalDrops - totalPreviousDrops;
    this.ws.addRow(['', '', 'Total', totalRedeemableDrops, totalPreviousDrops, totalLatestEarnedDrops, totalDrops]);
  }

  addInformationToWorksheet(): void {
    const header = ['#', 'Name', 'Level', 'Redeemable Pollens', 'Previous Pollens', 'Latest Earn', 'Total Pollens'];
    this.addTableHeaderRow(header);
    const rows = this.usersExport.map((user, idx) => this.buildRowInformation(user, idx));
    rows.forEach((userRowData, userIndex) => {
      this.interleaveRowStyle(userRowData, userIndex);
    });
  }

  addStyleToCell(cell: Cell, style: Style = {}): void {
    Object.keys(style).map(key => set(cell, key, get(style, key)));
  }

  addTableHeaderRow(header: string[]): void {
    const row = this.ws.addRow(header);
    row.eachCell(cell => {
      if (cell.value && cell.value !== '') {
        cell.font = STYLES.whiteBoldFont;
        cell.fill = STYLES.primaryTealFill;
      }
    });
  }

  addEmptyRow(): void {
    this.ws.addRow([]);
  }

  buildRowInformation(
    { accumulatedDrops, redeemableDrops, currentLevel, user: { id: userId, fullName, code } }: UserContributionLevelDTO,
    idx: number
  ): unknown[] {
    const previousDrops = this.getPreviousDrops([Number(userId)]);
    const latestEarnedDrops = accumulatedDrops - previousDrops;

    return [
      idx + 1,
      `${fullName} - ${code}`,
      CONTRIBUTION_LEVELS[currentLevel]?.name || NA,
      redeemableDrops,
      previousDrops,
      latestEarnedDrops,
      accumulatedDrops
    ];
  }

  mergeCells(row: Row, from: number, to: number): void {
    this.ws.mergeCells(`${row.getCell(from).address}:${row.getCell(to).address}`);
  }

  interleaveRowStyle(data: unknown[], index: number, callbackFn?: callbackFn): Row {
    return index % 2 === 0
      ? this.addRow(data, { fill: STYLES.lightTealFill }, callbackFn)
      : this.addRow(data, {}, callbackFn);
  }

  setColumnWidth(): void {
    this.ws.columns.forEach((column, index) => {
      column.width = this.columnWidths[index];
    });
  }

  addBorder(): void {
    this.ws.eachRow({ includeEmpty: true }, row => {
      row.eachCell({ includeEmpty: true }, cell => {
        const style = STYLES.thinGreyBorder;
        cell.border = {
          top: cell?.border?.top || style,
          bottom: cell?.border?.bottom || style,
          left: cell?.border?.left || style,
          right: cell?.border?.right || style
        };
      });
    });
  }

  get exportFileName(): string {
    return `HIVE_Export_By_Drop_${getCurrentDayString()}.xlsx`;
  }
}
